"use client"

import type React from "react"

import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import {
  ArrowLeft,
  Send,
  Fingerprint,
  Eye,
  Lock,
  MessageSquare,
  User,
  Mail,
  Sparkles,
  Target,
  Bomb,
} from "lucide-react"
import { Button } from "./ui/button"
import { Input } from "./ui/input"
import { Textarea } from "./ui/textarea"
import { cn } from "../lib/utils"
import { Footer } from "./layout/Footer"
import SEO from "../utils/SEO"

export default function ContactPage() {
  const navigate = useNavigate()
  const [formState, setFormState] = useState<"idle" | "sending" | "sent">("idle")
  const [secretHover, setSecretHover] = useState(false)
  const [nameValue, setNameValue] = useState("")
  const [emailValue, setEmailValue] = useState("")
  const [messageValue, setMessageValue] = useState("")

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setFormState("sending")

    try {
      const isLocalhost = ["localhost", "127.0.0.1", "::1"].includes(window.location.hostname)
      const endpoint = isLocalhost ? "http://localhost:3000/api/contact" : "/api/contact"

      await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: nameValue,
          email: emailValue,
          message: messageValue,
        }),
      }).catch(() => {
        // Fail silently
      })
    } catch {
      // Fail silently
    }

    setFormState("sent")
    // Reset form after showing success message
    setTimeout(() => {
      setFormState("idle")
      setNameValue("")
      setEmailValue("")
      setMessageValue("")
    }, 5000)
  }

  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      <SEO 
        title="Contact Us"
        description="Get in touch with the Spyfall Online team. We're here to help with any questions, feedback, or support you need."
        keywords="spyfall contact, game support, feedback, help, contact us"
      />

      {/* Header */}
      <header className="sticky top-0 z-40 w-full border-b border-gray-800 bg-black/80 backdrop-blur-sm">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 flex h-16 items-center justify-between">
          <div className="flex items-center gap-2">
            <button onClick={() => window.location.replace('/')} className="flex items-center gap-2">
              <ArrowLeft className="h-4 w-4" />
              <span className="text-xl font-bold text-red-500">SPYFALL</span>
            </button>
          </div>
          <Button 
            variant="outline" 
            className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
            onClick={() => navigate('/app')}
          >
            Escape Route
          </Button>
        </div>
      </header>

      <main className="flex-1 py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8 text-center">
            <div className="inline-block relative">
              <h1 className="text-4xl font-bold mb-2 relative z-10">
                <span className="text-red-500">TOP SECRET:</span> Contact HQ
              </h1>
              <div className="absolute -bottom-2 left-0 right-0 h-3 bg-red-500/30 -rotate-1 z-0"></div>
            </div>
            <p className="text-gray-400 mt-4 max-w-xl mx-auto">
              Need to report suspicious activity? Want to join our spy network? Or just want to say hi? Your message
              will self-destruct after sending.
              <span className="text-red-400">*</span>
            </p>
            <p className="text-xs text-gray-500 mt-2">
              <span className="text-red-400">*</span> Not really, but wouldn't that be cool?
            </p>
          </div>

          {/* Creator image with fun elements */}
          <div className="relative mb-12">
            <div className="absolute inset-0 bg-gradient-to-b from-red-500/0 via-red-500/5 to-red-500/0 rounded-full animate-pulse"></div>

            <div className="relative flex flex-col items-center">
              {/* Top secret stamp */}
              <div className="absolute -top-10 right-1/4 rotate-12 border-2 border-red-500 rounded px-2 py-1 text-xs font-bold text-red-500 z-20">
                TOP SECRET
              </div>

              {/* Creator image */}
              <div className="relative w-48 h-48 rounded-full overflow-hidden border-4 border-red-500 shadow-lg shadow-red-500/20">
                <img 
                  src="/imgs/spys/spy.webp?height=400&width=400" 
                  alt="The Mastermind" 
                  className="w-full h-full object-cover"
                />

                {/* Spy elements overlaid on image */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="relative w-full h-full">
                    <Fingerprint className="absolute top-2 left-2 h-6 w-6 text-red-500/70" />
                    <Target className="absolute bottom-2 right-2 h-8 w-8 text-red-500/70" />
                    <div className="absolute inset-0 border-4 border-dashed border-red-500/30 rounded-full"></div>
                  </div>
                </div>
              </div>

              {/* Creator title */}
              <div className="mt-4 text-center">
                <h2 className="text-xl font-bold">Agent JEFF</h2>
                <p className="text-gray-400">Master of Deception & Chief Fun Officer</p>
                <p className="text-gray-400 text-sm">It was me all along 😈</p>
                <div
                  className="mt-2 inline-flex items-center gap-1 px-3 py-1 bg-red-500/10 rounded-full text-sm text-red-400 cursor-pointer transition-all"
                  onMouseEnter={() => setSecretHover(true)}
                  onMouseLeave={() => setSecretHover(false)}
                >
                  <Lock className={cn("h-3 w-3 transition-all", secretHover ? "rotate-12" : "")} />
                  <span>Security Level: {secretHover ? "Wouldn't you like to know 😉" : "Classified"}</span>
                </div>
              </div>

              {/* Decorative elements */}
              <div className="absolute -left-16 top-1/2 transform -translate-y-1/2">
                <div className="relative">
                  <div className="w-12 h-12 rounded-full border border-red-500/30 animate-ping opacity-20"></div>
                  <div className="absolute inset-0 w-8 h-8 m-auto rounded-full border border-red-500/50 animate-ping opacity-30 animation-delay-300"></div>
                  <div className="absolute inset-0 w-4 h-4 m-auto rounded-full border border-red-500/70 animate-ping opacity-40 animation-delay-600"></div>
                </div>
              </div>

              <div className="absolute -right-16 top-1/2 transform -translate-y-1/2">
                <div className="relative">
                  <div className="w-12 h-12 rounded-full border border-red-500/30 animate-ping opacity-20"></div>
                  <div className="absolute inset-0 w-8 h-8 m-auto rounded-full border border-red-500/50 animate-ping opacity-30 animation-delay-300"></div>
                  <div className="absolute inset-0 w-4 h-4 m-auto rounded-full border border-red-500/70 animate-ping opacity-40 animation-delay-600"></div>
                </div>
              </div>
            </div>
          </div>

          {/* Contact form */}
          <div className="relative">
            {/* Decorative elements */}
            <div className="absolute -top-6 -left-6">
              <Bomb className="h-12 w-12 text-red-500/10" />
            </div>
            <div className="absolute -bottom-6 -right-6 rotate-12">
              <Sparkles className="h-12 w-12 text-red-500/10" />
            </div>

            <div className="bg-gray-900/70 backdrop-blur-sm rounded-lg border border-gray-800 p-6 relative overflow-hidden">
              {/* Form header */}
              <div className="mb-6 flex items-center gap-3">
                <div className="h-8 w-8 rounded-full bg-red-500 flex items-center justify-center">
                  <MessageSquare className="h-4 w-4" />
                </div>
                <h2 className="text-xl font-bold">Send Encrypted Message</h2>
              </div>

              {formState === "sent" ? (
                <div className="py-12 text-center">
                  <div className="inline-block mx-auto mb-4 rounded-full bg-green-500/20 p-4">
                    <Send className="h-8 w-8 text-green-500" />
                  </div>
                  <h3 className="text-xl font-bold mb-2">Message Transmitted!</h3>
                  <p className="text-gray-400">Your message has been securely delivered to our secret headquarters.</p>
                  <div className="mt-6 text-sm text-gray-500">
                    <div className="inline-block animate-pulse">This message will self-destruct in 5 seconds...</div>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="codename" className="block text-sm font-medium mb-1 flex items-center gap-1">
                        <User className="h-4 w-4 text-red-400" />
                        Your Codename
                      </label>
                      <Input
                        id="codename"
                        placeholder="Agent Smith"
                        className="bg-gray-800 border-gray-700 focus:border-red-500 focus:ring-red-500/20"
                        required
                        value={nameValue}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNameValue(e.target.value)}
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-medium mb-1 flex items-center gap-1">
                        <Mail className="h-4 w-4 text-red-400" />
                        Secure Communication Channel (Email)
                      </label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="agent@secretspy.com"
                        className="bg-gray-800 border-gray-700 focus:border-red-500 focus:ring-red-500/20"
                        required
                        value={emailValue}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmailValue(e.target.value)}
                      />
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-medium mb-1 flex items-center gap-1">
                        <Eye className="h-4 w-4 text-red-400" />
                        Classified Intelligence
                      </label>
                      <Textarea
                        id="message"
                        placeholder="I suspect the waiter at the café is actually a spy. He keeps asking everyone what they do for a living..."
                        className="bg-gray-800 border-gray-700 focus:border-red-500 focus:ring-red-500/20 min-h-[120px]"
                        required
                        value={messageValue}
                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setMessageValue(e.target.value)}
                      />
                    </div>

                    <div className="pt-2">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="secret"
                          className="rounded border-gray-700 text-red-500 focus:ring-red-500/20"
                          required
                        />
                        <label htmlFor="secret" className="text-sm text-gray-300">
                          I solemnly swear I am up to no good (and agree to the terms)
                        </label>
                      </div>
                    </div>
                  </div>

                  <div>
                    <Button
                      type="submit"
                      className="w-full bg-red-500 hover:bg-red-600 transition-all group relative overflow-hidden"
                      disabled={formState === "sending"}
                    >
                      <span className="relative z-10 flex items-center gap-2">
                        {formState === "sending" ? (
                          <>
                            <div className="h-4 w-4 rounded-full border-2 border-white border-t-transparent animate-spin"></div>
                            Encrypting...
                          </>
                        ) : (
                          <>
                            <Send className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                            Transmit Message
                          </>
                        )}
                      </span>
                      <span className="absolute inset-0 bg-gradient-to-r from-red-600 to-red-500 opacity-0 group-hover:opacity-100 transition-opacity"></span>
                    </Button>
                  </div>

                  <div className="text-center text-xs text-gray-500">
                    <p>All messages are encrypted with our proprietary "Totally Real Encryption™" technology</p>
                    <p className="mt-1">* Not actually encrypted. Please don't send real secrets.</p>
                  </div>
                </form>
              )}

              {/* Decorative corner elements */}
              <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-red-500"></div>
              <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-red-500"></div>
              <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-red-500"></div>
              <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-red-500"></div>
            </div>
          </div>

          {/* Fun facts */}
          <div className="mt-12">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Fingerprint className="h-5 w-5 text-red-500" />
              <span>Spy Facts</span>
              <span className="text-xs text-gray-500">(Definitely True*)</span>
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-gray-800/50 p-4 rounded-lg border border-gray-700 hover:border-red-500/50 transition-colors">
                <p className="text-sm text-gray-300">
                  Our office is located in an abandoned missile silo. The cafeteria serves excellent spy-themed meals
                  like "Surveillance Sushi" and "Wiretap Waffles."
                </p>
              </div>

              <div className="bg-gray-800/50 p-4 rounded-lg border border-gray-700 hover:border-red-500/50 transition-colors">
                <p className="text-sm text-gray-300">
                  All our employees must know at least 3 magic tricks and be able to disappear in a puff of smoke during
                  awkward conversations.
                </p>
              </div>

              <div className="bg-gray-800/50 p-4 rounded-lg border border-gray-700 hover:border-red-500/50 transition-colors">
                <p className="text-sm text-gray-300">
                  Our customer service team consists entirely of former international spies who got tired of the danger
                  and just wanted to help people have fun.
                </p>
              </div>

              <div className="bg-gray-800/50 p-4 rounded-lg border border-gray-700 hover:border-red-500/50 transition-colors">
                <p className="text-sm text-gray-300">
                  The creator of Spyfall has never been photographed without a disguise. Some say they're actually three
                  children in a trenchcoat.
                </p>
              </div>
            </div>

            <p className="text-xs text-gray-500 mt-2 text-center">* Not actually true. Or are they? 🕵️‍♂️</p>
          </div>

          <div className="mt-12 flex justify-center">
            <Button 
              variant="outline" 
              className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
              onClick={() => window.location.replace('/')}
            >
              Return to Safe House
            </Button>
          </div>
        </div>
      </main>

      <Footer />

      {/* Easter egg - floating spy */}
      <div className="fixed bottom-4 right-4 opacity-10 hover:opacity-70 transition-opacity cursor-pointer">
        <div className="relative w-12 h-12">
          <div className="absolute inset-0 rounded-full bg-gray-800 flex items-center justify-center">
            <Eye className="h-6 w-6 text-red-500" />
          </div>
          <div className="absolute -inset-1 border border-red-500 rounded-full animate-ping opacity-50"></div>
        </div>
      </div>
    </div>
  )
}

